import { useState, useCallback } from 'react'
import { api } from '../api/client.js'

export interface SipTrunkAclResponse {
  id: string
  status: boolean
  sipTrunkId: string | null
  aclName: string
  aclDescription: string | null
  createdBy: string | null
  updatedBy: string | null
  createdAt: string
  updatedAt: string
  createdByName: string | null
  updatedByName: string | null
}

export interface SipTrunkAclIpResponse {
  id: string
  aclId: string
  ipCidr: string
  createdBy: string | null
  createdAt: string
}

export interface SipTrunkAclCreateInput {
  status?: boolean
  sipTrunkId?: string | null
  aclName: string
  aclDescription?: string | null
  ips?: Array<{ ipCidr: string }>
}

export interface SipTrunkAclUpdateInput {
  status?: boolean
  sipTrunkId?: string | null
  aclName?: string
  aclDescription?: string | null
  ips?: Array<{ ipCidr: string }>
}

export interface SipTrunkAclSummary {
  total: number
  active: number
  inactive: number
}

export interface SipTrunkAclListResponse {
  items: SipTrunkAclResponse[]
  total: number
  limit: number
  offset: number
  summary: SipTrunkAclSummary
}

export function useSipTrunkAcl() {
  const [items, setItems] = useState<SipTrunkAclResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState<{ total: number; limit: number; offset: number; totalPages: number } | null>(null)
  const [summary, setSummary] = useState<SipTrunkAclSummary | null>(null)

  const fetchItems = useCallback(async (searchQuery = '', page = 1, limit = 20, silent = false) => {
    if (!silent) setLoading(true)
    try {
      const offset = (page - 1) * limit
      const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''
      const response = await api.get<SipTrunkAclListResponse>(`/admin/sip-trunk-acl?limit=${limit}&offset=${offset}${searchParam}`)
      setItems(response.items)
      setPagination({
        total: response.total,
        limit: response.limit,
        offset: response.offset,
        totalPages: Math.ceil(response.total / response.limit)
      })
      setSummary(response.summary ?? null)
    } catch (error) {
      console.error('Error fetching SIP trunk ACLs:', error)
      setItems([])
      setPagination(null)
      setSummary(null)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  const create = useCallback(async (data: SipTrunkAclCreateInput) => {
    try {
      const response = await api.post<SipTrunkAclResponse>('/admin/sip-trunk-acl', data)
      return response
    } catch (error) {
      console.error('Error creating SIP trunk ACL:', error)
      throw error
    }
  }, [])

  const update = useCallback(async (id: string, data: SipTrunkAclUpdateInput) => {
    try {
      const response = await api.put<SipTrunkAclResponse>(`/admin/sip-trunk-acl/${id}`, data)
      return response
    } catch (error) {
      console.error('Error updating SIP trunk ACL:', error)
      throw error
    }
  }, [])

  const remove = useCallback(async (id: string) => {
    try {
      const response = await api.delete<{ success: boolean }>(`/admin/sip-trunk-acl/${id}`)
      return response
    } catch (error) {
      console.error('Error deleting SIP trunk ACL:', error)
      throw error
    }
  }, [])

  const getById = useCallback(async (id: string): Promise<{ item: SipTrunkAclResponse; ips: SipTrunkAclIpResponse[] }> => {
    try {
      const response = await api.get<{ item: SipTrunkAclResponse; ips: SipTrunkAclIpResponse[] }>(`/admin/sip-trunk-acl/${id}`)
      return response
    } catch (error) {
      console.error('Error fetching SIP trunk ACL:', error)
      throw error
    }
  }, [])

  const refetch = useCallback(
    (searchQuery?: string, page?: number, limit?: number) => fetchItems(searchQuery, page, limit, true),
    [fetchItems],
  )

  return {
    items,
    loading,
    pagination,
    summary,
    refetch,
    refetchWithLoading: fetchItems,
    create,
    update,
    remove,
    getById,
  }
}
